import { generateClient } from 'aws-amplify/api';
import { getUrl } from 'aws-amplify/storage';
import type { Schema } from '../../amplify/data/resource';

const client = generateClient<Schema>();

export interface WineSummary {
  id: string;
  name: string;
  producer?: string;
  vintage?: number;
  style?: string;
  region?: string;
  myRating?: number;
  labelPhotoUrl?: string;
}

export async function getWinePhotoUrl(s3Key: string | null | undefined): Promise<string | undefined> {
  if (!s3Key) return undefined;
  try {
    const { url } = await getUrl({ path: s3Key, options: { expiresIn: 900 } });
    return url.toString();
  } catch {
    return undefined;
  }
}

export async function createOrGetWine(data: {
  name: string;
  producer?: string | null;
  region?: string | null;
  country?: string | null;
  vintage?: number | null;
  grapes?: string[];
  style?: string | null;
  body?: number | null;
  tannin?: number | null;
  acidity?: number | null;
  oak?: number | null;
  sweetness?: number | null;
  description?: string | null;
  aiTastingNotes?: string | null;
  foodPairings?: string[];
  servingTemp?: string | null;
  agingPotential?: string | null;
  priceRange?: string | null;
}): Promise<string> {
  // Try to find existing wine by name + producer + vintage
  const { data: existing } = await client.models.Wine.list({
    filter: {
      and: [
        { name: { eq: data.name } },
        ...(data.producer ? [{ producer: { eq: data.producer } }] : []),
        ...(data.vintage ? [{ vintage: { eq: data.vintage } }] : []),
      ],
    },
    limit: 1,
  });

  if (existing && existing.length > 0) {
    return existing[0].id;
  }

  // Create new wine record
  const { data: created } = await client.models.Wine.create({
    name: data.name,
    producer: data.producer ?? undefined,
    region: data.region ?? undefined,
    country: data.country ?? undefined,
    vintage: data.vintage ?? undefined,
    grapes: data.grapes ?? [],
    style: (data.style as Schema['Wine']['type']['style']) ?? undefined,
    body: data.body ?? undefined,
    tannin: data.tannin ?? undefined,
    acidity: data.acidity ?? undefined,
    oak: data.oak ?? undefined,
    sweetness: data.sweetness ?? undefined,
    description: data.description ?? undefined,
    aiTastingNotes: data.aiTastingNotes ?? undefined,
    foodPairings: data.foodPairings ?? [],
    servingTemp: data.servingTemp ?? undefined,
    agingPotential: data.agingPotential ?? undefined,
    priceRange: data.priceRange ?? undefined,
  });

  return created!.id;
}
